import React, { useState } from 'react';
import { useVerifyBadgeOwnershipMutation } from '../../slices/badgeApi';
import './EmailVerification.css';

const EmailVerification = ({ credentialId, onVerificationResult }) => {
  const [email, setEmail] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);
  
  const [verifyBadgeOwnership, { isLoading }] = useVerifyBadgeOwnershipMutation();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError(null);
    if (verificationResult) setVerificationResult(null);
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError(null);
    setVerificationResult(null);

    try {
      const result = await verifyBadgeOwnership({
        credentialId,
        email: email.trim()
      }).unwrap();

      setVerificationResult(result);
      if (onVerificationResult) {
        onVerificationResult(result);
      }
    } catch (err) {
      setError(err?.data?.message || err?.message || 'Verification failed. Please try again.');
    }
  };

  const resetVerification = () => {
    setEmail('');
    setVerificationResult(null);
    setError(null);
  };

  return (
    <div className="email-verification">
      <div className="email-verification__header">
        <h3>Verify Badge Ownership</h3>
        <p>This badge is valid. To ensure this badge actually belongs to you, enter your email address to verify ownership.</p>
      </div>

      {!verificationResult ? (
        <form onSubmit={handleVerifyEmail} className="email-verification__form">
          <div className="email-verification__input-group">
            <label htmlFor="verification-email" className="email-verification__label">
              Email Address
            </label>
            <input
              type="email"
              id="verification-email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter email address"
              className="email-verification__input"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="email-verification__error">
              <span className="email-verification__error-icon">❌</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !email.trim()}
            className="email-verification__button"
          >
            {isLoading ? (
              <>
                <div className="email-verification__loading-spinner"></div>
                Verifying...
              </>
            ) : (
              <>
                <span className="email-verification__button-icon">✉️</span>
                Verify Email
              </>
            )}
          </button>
        </form>
      ) : (
        <div className="email-verification__result">
          {verificationResult.verified ? (
            <div className="email-verification__success">
              <div className="email-verification__success-icon">✅</div>
              <h4>Ownership Verified</h4>
              <p>{verificationResult.message}</p>
              
              {verificationResult.badge_details && (
                <div className="email-verification__badge-details">
                  <h5>Badge Details:</h5>
                  <ul>
                    <li><strong>Name:</strong> {verificationResult.badge_details.name}</li>
                    <li><strong>Earned:</strong> {new Date(verificationResult.badge_details.earned_at).toLocaleDateString()}</li>
                    <li><strong>Issuer:</strong> {verificationResult.badge_details.issuer}</li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="email-verification__failure">
              <div className="email-verification__failure-icon">❌</div>
              <h4>Verification Failed</h4>
              <p>{verificationResult.message || 'The provided email does not match the badge owner.'}</p>
            </div>
          )}

          <button
            onClick={resetVerification}
            className="email-verification__reset-button"
          >
            Verify Another Email
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailVerification;